var _ = require('underscore');
var Backbone = require('backbone');
var userService = require('./../user/userService');
var routerUtils = require('./../utils/routerUtils');

module.exports = Backbone.Router.extend({
  /**
   * @property _authenticationRequired {Boolean} True if the router should attempt authentication, false otherwise.
   */
  _authenticationRequired: false,

  /**
   * @method initialize
   * @override
   */
  initialize: function() {
    _.bindAll(this, '_authentication');
  },

  /**
   * @property routeCallbacksWithoutAuthentication {String[]} List of route callbacks that should not have authentication applied to them.
   */
  _routeCallbacksWithoutAuthentication: [],

  /**
   * This is executed on every route but only requires a login if the route callback is NOT in _routeCallbacksWithoutAuthentication.
   * If the user is not authenticated then it asks the user to log in and then continues on to execute the route.
   * The benefit of this is that it applies to EVERY route (w/o calls in each) and preserves the url.
   *
   * See http://backbonejs.org/#Router-execute for more details.
   *
   * @method execute
   * @param callback the function to use for executing the route.
   * @param args the args for the callback.  Meant to be used by: callback.apply(this, args);
   * @param callbackString the name of the function callback as defined in the route list (right-hand side.  i.e. '_defaultRoute'... etc.)
   * @return {Boolean} true if this is not executing the route (so do the default thing) or false if we are taking over.
   */
  execute: function(callback, args, callbackString) {
    var router = this;
    var executeRouteCallbackFn = function() {
      var routeResult = Backbone.Router.prototype.execute.call(router, callback, args, callbackString);
      if (_.isFunction(router._executeOnNewRoute)) {
        router._executeOnNewRoute();
      }
      return routeResult;
    };
    var queryString = window.location.search.slice(1);
    var queryObject = routerUtils.parseQueryString(queryString);
    var originalUrl = queryObject.originalUrl;
    var redirectURL = window.location.href;
    if (originalUrl) {
      var currentHash = window.location.hash;
      redirectURL = routerUtils.cleanRedirectURL(originalUrl + currentHash);
      if (redirectURL) {
        executeRouteCallbackFn = function() {
          window.location = redirectURL;
        };
      }
    }

    var routeRequiresAuthentication = this._authenticationRequired && !_.contains(this._routeCallbacksWithoutAuthentication, callbackString);
    if (routeRequiresAuthentication) {
      this._authenticateUserThenContinueToRoute(executeRouteCallbackFn, redirectURL);
    } else {
      executeRouteCallbackFn();
    }
  },

  /**
   * Authenticate the user if they haven't been already, fetch the user-specific models and then continue to the route
   * callback.
   * @method _authenticateUserThenContinueToRoute
   * @param executeRouteCallbackFn
   * @param [redirectURL] {String} - url to redirect to after login.
   * @private
   */
  _authenticateUserThenContinueToRoute: function(executeRouteCallbackFn, redirectURL) {
    var userIsAlreadyAuthenticated = userService.getCurrentAuthentication();
    if (userIsAlreadyAuthenticated) {
      executeRouteCallbackFn();
    } else {
      // Display the loading page while we authenticate.
      this._loading();

      // It is up to _patientAuthentication() to execute the route when authentication is completed.
      //   E-reg authentication (patient and staff) ignores the executeRouteFn and if a patient logs in they
      //   are directed to the first page of ereg.
      //
      //   Pre-reg authentication executes the route on successful login.
      var router = this;
      var authenticateUserFn = function() {
        return router._authentication(executeRouteCallbackFn, redirectURL);
      };

      // Makes a server call to determine if the user is authenticated in the app.
      //   Resolves if the user is authenticated, reject is the user is not authenticated.
      userService.isAuthenticated()
      // fetchUserModel will be a noop if it is already fetched or in the process of fetching (but will resolve when fetching is complete).
        .then(userService.fetchUserModel)
        // executeRouteFn only executes if the user is authenticated and the fetchUserModel has completed successfully.
        // authenticateUser only executes if the user is not authenticated or fetching the user model fails.
        .then(executeRouteCallbackFn, authenticateUserFn);
    }
  },

  /**
   * This displays a full-page loading screen while we wait for server data to load.
   * This should only be used in the cases where we do not have another perspective to show while data is loading.
   * This is not implemented in admin tool.
   * @method _loading
   * @private
   */
  _loading: _.noop,

  /**
   * Initializes the authentication perspective and renders it
   *
   * @method _authentication
   * @private
   */
  _authentication: _.noop,

  /**
   * This binds to the default route.  Override to navigate to other perspectives.
   * @method _404
   * @private
   */
  _404: function() {
    this._defaultRoute();
  },

  _defaultRoute: _.noop
});

